import { jsPDF as PDF } from "jspdf"
import "./utils/pdfFonts.utils.js"
import { buildHeaderSection } from "./components/buildHeaderSection"
import { buildSummarySection } from "./components/buildSummarySection"
import { buildHourConsumptionSection } from "./components/buildHourConsumptionSection.js"
import { buildBurnchartSection } from "./components/buildBurnchartSection.js"
import { buildNotesSection } from "./components/buildNotesSection.js"
import { buildPointProgressSection } from "./components/buildPointProgressSection.js"
import { buildBudgetStateSection } from "./components/buildBudgetStateSection.js"
import {
  getClientReportTimeConsumption,
  getClientSprintPointProgressBySprintId,
  getDownloadClientReportSprintSummaryNew,
  getClientSprintBudgetStateBySprintId,
  getClientReportBurnchartDataByClientIdAndPeriod,
  getClientReportNotes,
  getLastSprintIdByPeriod,
} from "../../../services/clientReport.service"
import { calculateBurnchartProjection } from "../../../utils/clientReport.utils"

import { getValidLuxonDate } from "../../../utils/date.utils"
import { CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY } from "../../../consts/modules/clientReport.consts"

const getPeriodLabel = (startDate, endDate) => {
  if (!startDate) return "En curso"

  return `${getValidLuxonDate(startDate)?.toFormat("dd/MM/yyyy")} - ${
    getValidLuxonDate(endDate)?.toFormat("dd/MM/yyyy") || "En curso"
  }`
}

const CLIENT_REPORT_PDF_CONFIG = {
  orientation: "p",
  unit: "mm",
  format: [216, 340],
}

export const generateClientReportPdf = async ({
  sprintData,
  visibleMap,
  clientName,
  clientLogo,
  chartRefs,
  mode = "download",
  period,
  reportCreatedAt,
  extraData,
}) => {
  const doc = new PDF(CLIENT_REPORT_PDF_CONFIG)

  const refsToCapture = Object.entries(chartRefs.current)
    .filter(([key, ref]) => ref.current)
    .reduce((acc, [key, ref]) => {
      acc[key] = ref.current
      return acc
    }, {})

  let currentY = 20

  let sprintId = sprintData.id

  if (
    period.toUpperCase() === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ||
    period.toUpperCase() === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.CYCLE
  ) {
    const lastSprintResponse = await getLastSprintIdByPeriod(
      sprintData.id,
      period.toUpperCase()
    )

    if (lastSprintResponse.success && lastSprintResponse.result) {
      sprintId = lastSprintResponse.result
    }
  }

  const response = await getDownloadClientReportSprintSummaryNew(
    sprintData.id,
    period.toUpperCase(),
    sprintData.clientId
  )

  const sprintSummaryData = response?.success ? response.result : {}

  currentY = await buildHeaderSection(
    doc,
    sprintData,
    currentY,
    clientName,
    clientLogo,
    getPeriodLabel(sprintSummaryData?.startDate, sprintSummaryData?.endDate)
  )

  currentY = await buildSummarySection(
    doc,
    sprintData,
    currentY,
    sprintSummaryData,
    period
  )

  if (visibleMap.HOUR_CONSUMPTION) {
    const response = await getClientReportTimeConsumption(sprintId)
    const timeConsumptionData = response?.success ? response.result : {}
    currentY = await buildHourConsumptionSection(
      doc,
      timeConsumptionData,
      currentY
    )
  }

  if (visibleMap.POINT_PROGRESS) {
    const response = await getClientSprintPointProgressBySprintId(sprintId)

    const pointProgressData = response?.success ? response.result : {}

    currentY = await buildPointProgressSection(
      doc,
      sprintData,
      currentY,
      pointProgressData,
      refsToCapture,
      period,
      extraData
    )
  }

  const getBurnchartSummaryForPDF = async (sprintData) => {
    try {
      const response = await getClientReportBurnchartDataByClientIdAndPeriod(
        sprintData?.clientId,
        sprintData?.id,
        period.toUpperCase()
      )

      if (!response.success || !response.result?.length) {
        return {
          toDevelop: 0,
          developed: 0,
          projected: 0,
          optimistic: 0,
          pessimistic: 0,
        }
      }

      const result = response.result
      const lastSprint = result[result.length - 1]

      const { estimatedSprint, optimisticSprint, pessimisticSprint } =
        calculateBurnchartProjection({
          realSprints: result,
          lastSprint,
        })

      const totalPointsToComplete = lastSprint?.totalPointsToComplete ?? 0

      const totalCompletedPoints = lastSprint?.totalCompletedPoints ?? 0

      return {
        toDevelop: `${totalPointsToComplete} pts`,
        developed: `${totalCompletedPoints} pts`,
        projected: Math.round(estimatedSprint ?? 0),
        optimistic: Math.round(optimisticSprint ?? 0),
        pessimistic: Math.round(pessimisticSprint ?? 0),
      }
    } catch (error) {
      console.error("Error building burnchart summary:", error)

      return {
        toDevelop: 0,
        developed: 0,
        projected: 0,
        optimistic: 0,
        pessimistic: 0,
      }
    }
  }

  if (visibleMap.BURNCHART) {
    const burnchartSummary = await getBurnchartSummaryForPDF(sprintData)
    currentY = await buildBurnchartSection(
      doc,
      currentY,
      refsToCapture,
      burnchartSummary,
      clientName,
      clientLogo,
      sprintData,
      buildHeaderSection,
      extraData
    )
  }

  if (visibleMap.BUDGET_STATE) {
    const responsebudget = await getClientSprintBudgetStateBySprintId(sprintId)
    const budgetData = responsebudget?.success ? responsebudget.result : {}
    currentY = await buildBudgetStateSection(
      doc,
      sprintData,
      visibleMap,
      currentY,
      budgetData,
      refsToCapture,
      extraData
    )
  }

  if (visibleMap.NOTES) {
    const normalizedPeriod = period?.toUpperCase()

    const notesAllowed =
      normalizedPeriod === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ||
      normalizedPeriod === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.CYCLE

    if (notesAllowed) {
      try {
        const response = await getClientReportNotes(
          sprintData.id,
          period.toUpperCase()
        )

        const notes = response?.success ? response?.result?.reportNotes : ""

        currentY = await buildNotesSection(doc, currentY, notes)
      } catch (error) {
        console.error("Error loading notes:", error)
      }
    }
  }

  const pageCount = doc.getNumberOfPages()

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)

    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    const sideMargin = 10

    const lineY = pageHeight - 18
    const footerY = pageHeight - 13

    doc.setDrawColor("#006AA6")
    doc.setLineWidth(0.5)
    doc.line(sideMargin, lineY, pageWidth - sideMargin, lineY)

    doc.setTextColor("#24292F")

    doc.setFont("Mulish-Regular", "normal")
    doc.setFontSize(8)
    doc.text("sisu.mx", sideMargin, footerY)

    doc.setTextColor("#737E8D")
    doc.text(
      `Reporte creado en ${reportCreatedAt}`,
      pageWidth - sideMargin,
      footerY,
      { align: "right" }
    )
  }

  if (mode === "download") {
    let fileName = "Sprint_Report.pdf"

    if (period.toUpperCase() === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK)
      fileName = "Block_Report.pdf"
    if (period.toUpperCase() === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.CYCLE)
      fileName = "Cycle_Report.pdf"

    doc.save(fileName)
    return
  }

  if (mode === "version") {
    return doc.output("blob")
  }
}
